// Customize mode for a criteria form (General Process / Report): the prompts
// as draggable cards with a ⚙ each, a drop zone at the end, and the hidden
// prompts in a tray. The host owns the model and turns each gesture into an
// overlay diff; this component only draws and reports.

import { useState } from 'react'
import { Lock } from 'lucide-react'
import { CustomizedDot, GearButton, Grip, HiddenTray, VIOLET, VIOLET_LINE, getDragData, setDragData } from './ui'
import { tw } from '../testHooks'

export interface PromptCard {
  key: string
  label: string
  /** The value it shows / runs with (its default). */
  valueText: string
  hidden: boolean
  protect: boolean
  customized: boolean
  mandatory?: boolean
}

export default function PromptCustomizer({
  items, onReorder, onOpen, onShow, openKey, hint,
  trayTitle = 'Hidden prompts', trayHint = 'A hidden prompt always runs on its default value.', noun = 'prompt',
}: {
  trayTitle?: string
  trayHint?: string
  /** What a card is called in hints ("prompt" / "column"). */
  noun?: string
  items: PromptCard[]
  /** New order of the VISIBLE prompts' keys. */
  onReorder: (keys: string[]) => void
  onOpen: (key: string, anchor: HTMLElement) => void
  onShow: (key: string) => void
  openKey?: string | null
  hint?: string
}) {
  const shown = items.filter((i) => !i.hidden)
  const hidden = items.filter((i) => i.hidden)
  const [over, setOver] = useState<string | null | undefined>(undefined)
  const drop = (data: string, beforeKey: string | null) => {
    setOver(undefined)
    const [kind, key] = data.split('|')
    if (kind !== 'prompt' || key === beforeKey) return
    const keys = shown.map((i) => i.key).filter((k) => k !== key)
    const at = beforeKey ? keys.indexOf(beforeKey) : -1
    keys.splice(at < 0 ? keys.length : at, 0, key)
    onReorder(keys)
  }
  return (
    <div {...tw('screen', 'customize-prompts')} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 13.5, color: 'var(--color-text-secondary)' }}>
        {hint || `Drag a ${noun} to reorder it · ⚙ to rename it, hide it, lock it or give it a default`}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px 18px' }}>
        {shown.map((it) => {
          const open = openKey === it.key
          return (
            <div key={it.key} draggable
              onDragStart={(e) => setDragData(e, `prompt|${it.key}`)}
              onDragOver={(e) => { e.preventDefault(); setOver(it.key) }}
              onDrop={(e) => { e.preventDefault(); drop(getDragData(e), it.key) }}
              {...tw('field', it.key, { customize: true })}
              style={{
                position: 'relative', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0,
                padding: '7px 34px 9px 9px', borderRadius: 7, background: '#fdfcff',
                border: `1px ${open ? 'solid' : 'dashed'} ${open ? '#7c3aed' : VIOLET_LINE}`,
                boxShadow: over === it.key ? `inset 4px 0 0 ${VIOLET}` : undefined,
              }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Grip size={12} />{it.label}{it.mandatory ? ' *' : ''}
                {it.protect && <Lock size={11} aria-label="Read-only" />}
                {it.customized && <CustomizedDot />}
              </span>
              <span style={{
                height: 30, boxSizing: 'border-box', border: '1px solid var(--color-border)', borderRadius: 6,
                padding: '0 10px', display: 'flex', alignItems: 'center', fontSize: 14.5, overflow: 'hidden',
                whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                background: it.protect ? 'var(--color-bg-secondary)' : 'var(--color-bg)',
                color: it.valueText ? 'var(--color-text)' : 'var(--color-text-secondary)',
              }}>{it.valueText || (noun === 'prompt' ? '—' : 'column')}</span>
              <span style={{ position: 'absolute', top: 5, right: 5 }}>
                <GearButton label={`Change ${it.label}`} active={open} onClick={(el) => onOpen(it.key, el)} />
              </span>
            </div>
          )
        })}
        <div
          onDragOver={(e) => { e.preventDefault(); setOver(null) }}
          onDragLeave={() => setOver(undefined)}
          onDrop={(e) => { e.preventDefault(); drop(getDragData(e), null) }}
          style={{
            minHeight: 58, borderRadius: 7, border: `1px dashed ${over === null ? VIOLET : 'var(--color-border)'}`,
            background: over === null ? '#f5f3ff' : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: 'var(--color-text-secondary)',
          }}>Drop here to move to the end</div>
      </div>
      <HiddenTray title={trayTitle}
        hint={trayHint}
        items={hidden.map((it) => ({
          key: it.key,
          label: it.valueText && noun === 'prompt' ? `${it.label} · runs as ${it.valueText}` : it.label,
          onShow: () => onShow(it.key),
        }))} />
    </div>
  )
}
